import { motion } from "framer-motion";
import { MapPin, ExternalLink, Navigation } from "lucide-react";
import { BRANCHES, OPENING_HOURS, WEEK_DAYS, formatTime } from "@/data/salon";

export const Branches = () => {
  return (
    <section id="branches" data-testid="branches-section" className="py-24 sm:py-32 bg-elevated">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-pink">Visit Us</span>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05]">
            Three studios, <span className="italic text-pink">one standard.</span>
          </h2>
          <p className="mt-5 text-muted font-light leading-relaxed">
            Find us across Motherwell and Glasgow. Book your branch online in seconds — or drop in, walk-ins are always welcome.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {BRANCHES.map((b, i) => (
            <motion.article
              key={b.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`branch-card-${b.id}`}
              className="group flex flex-col overflow-hidden rounded-3xl border border-pink/25 bg-surface"
            >
              <div className="relative h-52 overflow-hidden border-b border-pink/15">
                <iframe
                  title={`Map of Fabrowz ${b.name}`}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(b.address)}&z=15&output=embed`}
                  className="h-full w-full grayscale-[0.2] transition-all duration-500 group-hover:grayscale-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-serif text-2xl text-ink">{b.name}</h3>
                <p className="mt-2 flex items-start gap-2 text-sm text-muted">
                  <MapPin className="h-4 w-4 text-pink mt-0.5 shrink-0" />
                  {b.address}
                </p>
                <div className="mt-5 rounded-2xl border border-pink/15 bg-blush/50 p-4" data-testid={`branch-hours-${b.id}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-pink">Opening hours</p>
                  <div className="mt-3 space-y-1.5">
                    {WEEK_DAYS.map(({ day, label }) => {
                      const hours = OPENING_HOURS[b.id]?.[day];
                      return (
                        <div key={label} className="flex items-center justify-between gap-4 text-xs">
                          <span className="font-mono uppercase tracking-[0.08em] text-muted">{label.slice(0, 3)}</span>
                          <span className="text-ink">{hours ? `${formatTime(hours[0])} – ${formatTime(hours[1])}` : "Closed"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-2 pt-2">
                  <a
                    href={b.treatwell}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`branch-treatwell-${b.id}`}
                    className="flex items-center gap-2 rounded-full bg-pink px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-cream hover:bg-ink transition-colors"
                  >
                    Book on Treatwell <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(b.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`branch-directions-${b.id}`}
                    className="flex items-center gap-2 rounded-full border border-pink/30 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-ink hover:border-pink transition-colors"
                  >
                    <Navigation className="h-3.5 w-3.5 text-pink" /> Directions
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
